import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Student photograph capture.
 *
 * The legacy form used webcam.js to grab a JPEG from the camera and post it as a base64
 * string. The API still takes exactly that (`base64Image`), so this replaces the plugin
 * with getUserMedia and a canvas — no dependency, and it works on phones.
 *
 * A file picker sits alongside it because getUserMedia needs a secure context: on plain
 * http, or on a machine with no camera, the picker is the only way through. The legacy form
 * had no fallback at all, so this is strictly more usable.
 */
export function PhotoCapture({
  value,
  onChange,
  existingPhotoPath,
}: {
  /** data:image/jpeg;base64,… or empty. */
  value: string;
  onChange: (base64: string) => void;
  /** File name already stored against the student, if any. */
  existingPhotoPath?: string | null;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setLive(false);
  }, []);

  // Releasing the camera on unmount matters: leaving it open keeps the recording
  // indicator lit and blocks other apps from using it.
  useEffect(() => stop, [stop]);

  async function start() {
    setError(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser cannot reach the camera here. Use “Choose file” instead — a camera needs an https connection.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: false,
      });

      streamRef.current = stream;

      // Attaching happens in the effect below, not here. The <video> used to be mounted only
      // while `live` was true, so at this point videoRef.current was still null - React had
      // not re-rendered yet - and the stream was dropped on the floor. The preview stayed
      // blank while the camera light was on.
      setLive(true);
    } catch {
      setError('Could not open the camera. Check the browser permission, or use “Choose file”.');
    }
  }

  // The element is always mounted, so this runs with a real ref in hand.
  useEffect(() => {
    const video = videoRef.current;
    if (!live || !video || !streamRef.current) return;

    video.srcObject = streamRef.current;

    // Safari rejects play() if the element is not yet laid out; a failed autoplay is not
    // worth an error message, the user can simply press Capture.
    void video.play().catch(() => undefined);
  }, [live]);

  function capture() {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // JPEG at 0.9 to match what the old webcam.js capture produced.
    onChange(canvas.toDataURL('image/jpeg', 0.9));
    stop();
  }

  function pickFile(file: File) {
    setError(null);

    if (!file.type.startsWith('image/')) {
      setError('That file is not an image.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsDataURL(file);
  }

  const preview = value || (existingPhotoPath ? `/Resources/Photos/${existingPhotoPath}` : null);

  return (
    <div className="field">
      <label>Photograph</label>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div
          style={{
            width: 200, height: 150, border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', overflow: 'hidden',
            background: 'var(--surface-2)', display: 'grid', placeItems: 'center',
          }}
        >
          {/* Always mounted, hidden when idle: the ref has to exist before getUserMedia
              resolves, or there is nothing to hand the stream to. */}
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              display: live ? 'block' : 'none',
            }}
          />

          {!live && (preview ? (
            <img
              src={preview}
              alt="Student"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              // A missing file falls through to the SPA's index.html, which renders as a
              // broken image. Treat that as "no photo" instead.
              onError={(event) => { event.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>No photo</span>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!live && (
            <button type="button" className="btn btn--sm" onClick={() => void start()}>
              Use camera
            </button>
          )}

          {live && (
            <>
              <button type="button" className="btn btn--sm btn--accent" onClick={capture}>Capture</button>
              <button type="button" className="btn btn--sm" onClick={stop}>Cancel</button>
            </>
          )}

          <label className="btn btn--sm" style={{ cursor: 'pointer' }}>
            Choose file
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) pickFile(file);
                event.target.value = '';
              }}
            />
          </label>

          {value && (
            <button type="button" className="btn btn--sm" onClick={() => onChange('')}>
              Discard
            </button>
          )}
        </div>
      </div>

      {error && <span className="field__error">{error}</span>}
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        Optional. Saved as a JPEG alongside the student record.
      </span>
    </div>
  );
}
